import { Component } from 'react';
import PropTypes from 'prop-types';

import { withAnalyticsEvents } from '@atlaskit/analytics-next';

import { TOUCH, END } from '@confluence/navdex';
import { getLogger } from '@confluence/logger';
import { getDwellTimeTracker } from '@confluence/dwell';
import { DWELL_TIME_START_EXPERIENCE } from '@confluence/experience-tracker';

import { LastSuccessfulContentTracker } from './LastSuccessfulContentTracker';
import { PAGE_TYPES, IFRAME_TYPES } from './dwellConstants';

const logger = getLogger('DwellTime');

const NAV_SESSION_SUCCESS_THRESHOLD = 40000;

export class DwellTimeComponentClass extends Component {
	static propTypes = {
		createAnalyticsEvent: PropTypes.func.isRequired,
		spaceId: PropTypes.string.isRequired,
		contentId: PropTypes.string.isRequired,
		contentIdLoading: PropTypes.bool.isRequired,
		contentType: PropTypes.string.isRequired,
		isSpace: PropTypes.bool.isRequired,
		experienceTracker: PropTypes.object.isRequired,
		lastContentTracker: PropTypes.instanceOf(LastSuccessfulContentTracker),
		searchSessionId: PropTypes.string,
		additionalAnalytics: PropTypes.object,
		isUserViewingOwnPersonalSpace: PropTypes.bool,
		spaceType: PropTypes.string,
		isInitial: PropTypes.bool,
	};

	componentDidMount() {
		this.onInitialLoad();
	}

	shouldComponentUpdate(nextProps) {
		// Only update if the content is actually changing
		return (
			!nextProps.contentIdLoading &&
			(this.props.spaceId !== nextProps.spaceId || this.props.contentId !== nextProps.contentId)
		);
	}

	componentDidUpdate() {
		this.onContentTransition();
	}

	async componentWillUnmount() {
		const { contentType } = this.props;
		const dwellTimeTracker = await getDwellTimeTracker(IFRAME_TYPES.has(contentType));
		dwellTimeTracker.stop();
	}

	onInitialLoad = () => {
		this.startDwell();
	};

	onContentTransition = () => {
		this.startDwell();
	};

	async startDwell() {
		const { isSpace, spaceId, contentId, contentType, searchSessionId, additionalAnalytics } =
			this.props;
		await getDwellTimeTracker(IFRAME_TYPES.has(contentType)).then(async (tracker) => {
			try {
				if (isSpace) {
					await tracker.startSpace(
						spaceId,
						this.sendDwellEvent,
						searchSessionId,
						additionalAnalytics,
					);
					this.props.experienceTracker.succeed({
						name: DWELL_TIME_START_EXPERIENCE,
					});
				} else {
					await tracker.startContent(
						spaceId,
						contentId,
						contentType,
						this.sendDwellEvent,
						searchSessionId,
						additionalAnalytics,
					);
					this.props.experienceTracker.succeed({
						name: DWELL_TIME_START_EXPERIENCE,
					});
				}
			} catch (error) {
				this.props.experienceTracker.fail({
					name: DWELL_TIME_START_EXPERIENCE,
					error,
				});
			}
		});
	}

	setDwellAttributes = (dwellData) => {
		const { contentType, isUserViewingOwnPersonalSpace, spaceType, isInitial } = this.props;

		if (
			!dwellData.attributes.finalDwellEvent &&
			dwellData.attributes.dwellTime <= NAV_SESSION_SUCCESS_THRESHOLD * 1.5
		) {
			dwellData.attributes.navdexPointType =
				dwellData.attributes.dwellTime < NAV_SESSION_SUCCESS_THRESHOLD ? TOUCH : END;
		}

		if (PAGE_TYPES.has(contentType)) {
			dwellData.attributes.pageType = contentType;
		}

		dwellData.attributes.spaceType = spaceType;
		dwellData.attributes.isUserViewingOwnPersonalSpace = isUserViewingOwnPersonalSpace;

		dwellData.attributes.isInitial = isInitial;

		return dwellData;
	};

	sendGasV3 = (dwellData) => {
		const dataWithAttributes = this.setDwellAttributes(dwellData);
		const dataWithAdjustedDuration = this.adjustDuration(dataWithAttributes);

		this.props
			.createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: dataWithAdjustedDuration,
			})
			.fire();
	};

	adjustDuration = (dwellData) => {
		const { lastContentTracker } = this.props;

		const lastContent = lastContentTracker.getLastSuccessfulContent();
		logger.debug`lastContent ${lastContent}`;

		const isItCurrent = lastContent && lastContent.id === dwellData.objectId;

		const dwellTimeWithoutLoad = isItCurrent
			? dwellData.attributes.dwellTime - lastContent.duration
			: undefined;

		const dataWithAdjustedDuration = {
			...dwellData,
			attributes: {
				...dwellData.attributes,
				dwellTimeWithoutLoad,
			},
		};

		logger.debug`adjusted dwell time ${dwellTimeWithoutLoad} for contentId ${dwellData.objectId} instead of ${dwellData.attributes.dwellTime}`;

		return dataWithAdjustedDuration;
	};

	sendDwellEvent = (dwellData) => {
		this.sendGasV3(dwellData);
	};

	render() {
		return null;
	}
}

export default withAnalyticsEvents()(DwellTimeComponentClass);
